import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { WebIcon } from '@/assets';
import type { TerritorialAuthorityStat } from '@/features/mein-ort/components/territorial-authority/territorialAuthorityFacts';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import type { SVGComponent } from '@/types';

const displayUrl = (raw: string): string =>
  raw.trim().replace(/^https?:\/\//i, '');

type Tile = {
  key: string;
  icon: SVGComponent;
  value: string;
  label: string;
  onPress?: () => void;
};

type Props = {
  organization: ClientOrganization;
  stats: TerritorialAuthorityStat[];
};

export const TerritorialAuthorityStatsGrid: FC<Props> = ({
  organization,
  stats
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace.territorialAuthority.stats'
  });

  const handleOpenWebsite = useCallback(() => {
    handleOpenUrl(organization.url);
  }, [organization.url]);

  const tiles = useMemo(() => {
    const result: Tile[] = [];
    for (const stat of stats) {
      result.push({
        key: stat.id,
        icon: stat.icon,
        value: stat.value,
        label: stat.id === 'einwohner' ? t('einwohnende') : t('flaeche')
      });
    }
    if (!isEmptyOrNull(organization.url)) {
      result.push({
        key: 'webseite',
        icon: WebIcon,
        value: displayUrl(organization.url ?? ''),
        label: t('webseite'),
        onPress: handleOpenWebsite
      });
    }
    return result;
  }, [stats, organization.url, t, handleOpenWebsite]);

  if (tiles.length === 0) {
    return <></>;
  }

  return (
    <View style={styles.grid}>
      {tiles.map((tile) => {
        const Icon = tile.icon;
        const content = (
          <>
            <Icon fill={theme.colors.onSurface} width={24} height={24} />
            <Text
              style={[
                styles.value,
                tile.onPress !== undefined && styles.valueLink
              ]}
              numberOfLines={1}
            >
              {tile.value}
            </Text>
            <Text style={styles.label} numberOfLines={1}>
              {tile.label}
            </Text>
          </>
        );
        return (
          <View key={tile.key} style={styles.tile}>
            {tile.onPress ? (
              <TouchableRipple
                onPress={tile.onPress}
                rippleColor={theme.rippleColors.onSurface}
                borderless
                style={styles.tileTouch}
              >
                {content}
              </TouchableRipple>
            ) : (
              content
            )}
          </View>
        );
      })}
    </View>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    grid: {
      flexDirection: 'row',
      paddingHorizontal: 32,
      gap: 8
    },
    tile: {
      flex: 1,
      alignItems: 'flex-start'
    },
    tileTouch: {
      alignItems: 'flex-start',
      padding: 4,
      margin: -4,
      borderRadius: 8
    },
    value: {
      marginTop: 4,
      fontSize: 14,
      fontWeight: '700',
      lineHeight: 20,
      color: theme.colors.onSurface
    },
    valueLink: {
      color: theme.colors.primary
    },
    label: {
      fontSize: 12,
      lineHeight: 16,
      color: theme.colors.onSurfaceVariant
    }
  });
